import axios from "axios";
import {getCurrentCustomerId, getCustomerSwitchingStatus, redirectToSignIn} from "app/root/Root";
import {API_URL, IAM_FRONT_END_URL, PRODUCT_ID, PRODUCT_CONTEXT, IS_DEV} from "common-ui-components/components/utils/Config";
import {getAuthToken} from "../../accounts/framework/AuthTokenHandler";
import { notify } from "../notification/Notification";
import { Failure } from "../notification/templates";

/**
 * API is an axios instance that is used to make requests to the backend.
 * It adds the auth token to the headers of each request.
 * It handles the 401 error by clearing the auth token and reloading the page.
 * It has an interceptor that logs the time taken for each request.
 *
 * @returns {AxiosInstance} An axios instance that is used to make requests to the backend.
 *
 * @example
 * .env
 * REACT_APP_DEV_API_URL=http://localhost:8000
 * REACT_APP_PROD_API_URL=https://api.example.com
 *
 * file uses API
 *
 * import API from "common-ui-components/components/utils/API";
 *
 * API.get("/api/v1/users").then((response) => {
 *  //console.log(response);
 * }
 */

const API = axios.create({
    baseURL: API_URL,
    headers: {
        "Accept": "application/json",
    },
    withCredentials: true,
});

// Add Authorization header dynamically before each request
API.interceptors.request.use((config) => {
    // Request will NOT go to server utill customer id update
    if (getCustomerSwitchingStatus()) 
    {
        return Promise.reject({
            message: "Blocked before API call"
        });
    }
    config.metadata = { startTime: new Date() };

// Log the API request URL before getting the token
    const fullUrl = `${config.baseURL || ''}${config.url || ''}`;

    // Retrieve and attach token
    const token = getAuthToken();
    if (token)
        config.headers["Authorization"] = `Bearer ${token}`;
    else {
        delete config.headers["Authorization"];
    }


    config.headers["customerId"] = getCurrentCustomerId();
    
    if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
    } else {
        config.headers["Content-Type"] = "application/json";
    }

    // Preserve existing params and allow override
    config.params = {
        ...(config.params || {}),
        productId: config.params?.productId || PRODUCT_ID
    };

    return config;
});
// Handle responses and errors globally
API.interceptors.response.use(
    (response) => {
        response.config.metadata.endTime = new Date();
        response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
//        if (window.location.hostname === "localhost") {
//            console.log(`Path ${response.config.url} taken ${response.duration}ms`);
//        }
        const content_header = response.headers["content-disposition"];
        if (content_header) {
            return response
        }
        return response.data; // Return only the data portion of the response
    },
    (error) => {
        const {response, config} = error;
        const responseDataMsg = response?.data?.message ?? '';
        if (!response)
        {
            // Network or other errors without a response
            console.error("Network error or no response:", error);
            return Promise.reject(error);
        }

        if (response.status === 307) 
        {
            window.location.href = response.data.redirectUrl;
        }

        if (response.status === 403) 
        {
            notify(<Failure>You don't have access to the requested resource.</Failure>)
            // setTimeout(() => window.location.reload(), 1000);
        }

        else if (response.status === 406) 
        {
            if (config?.no406Redirect)
                return Promise.reject(error);
            sessionStorage.setItem("406RedirectedToTryProductPage", "true");
            window.location.href = `/${PRODUCT_CONTEXT}/try-product`;
        }

        else if (response.status === 401)
        {
            redirectToSignIn();
        }

        else if (response.status === 423) 
        {
            let redirectTo = `${IAM_FRONT_END_URL}/auth/waitingForApproval`;
            if(responseDataMsg.startsWith('PERMISSION_DENIED: EmailNotVerified:')) {
                const parts = responseDataMsg.split(":");
                const email = parts.length > 2 ? parts[2] : "";
                redirectTo = `${IAM_FRONT_END_URL}/auth/verify-email?email=${email}`;
            } else if(responseDataMsg.startsWith('PERMISSION_DENIED: ApprovalDeniedForIP:')) {
                redirectTo = `${IAM_FRONT_END_URL}/auth/waitingForApproval?product=${PRODUCT_CONTEXT}&reason=ip`;
            } else if(responseDataMsg.startsWith('PERMISSION_DENIED: ApprovalDeniedForEmail:')) {
                redirectTo = `${IAM_FRONT_END_URL}/auth/waitingForApproval?product=${PRODUCT_CONTEXT}&reason=email`;
            } else {
                redirectTo = `${IAM_FRONT_END_URL}/auth/waitingForApproval?product=${PRODUCT_CONTEXT}`;
            }
            if(IS_DEV) {
                // Append a query param flag so the router knows this 423 was explicitly intended
                redirectTo += `&status=pending`;
            }

            window.location.href = redirectTo;

            // Halt any remaining execution inside the active React components
            return new Promise(() => {});
        }

        else if (response.status === 412)
        {
            // Email not verified case:
            window.location.href = `${IAM_FRONT_END_URL}/auth/verify-email`;
        }

        else if (response.status === 503)
        {
            notify(<Failure>System upgrade in progress. Please try again shortly.</Failure>);
        }
        
        else {
            console.error(error);
        }
        return Promise.reject(error);
    }
);

export default API;